import { ReactElement } from 'react';
import { useIntl } from 'react-intl';

import ContentChip from '@/components/app/cards/ContentChip';
import FaveHeart from '@/components/app/contentPlayer/FaveHeart';
import { useGuideActivation } from '@/hooks/app/useGuideActivation';
import { useGuideDuration } from '@/hooks/app/useGuideDuration';
import { useGuideInfo } from '@/hooks/app/useGuideInfo';
import circleCheck from 'icons/circle-check.svg';
import ringOutline from 'icons/ring-outline.svg';

import { useMediaPlayerContext } from '../../contentPlayer/MediaPlayerProvider';
import messages from './messages';
import {
	ClickableWrapper,
	Description,
	Duration,
	FaveHeartContainer,
	RowContent,
	StatusGlyph,
	TextBlock,
	Title,
	TrailingSlot,
	Waveform,
	Wrapper,
} from './styles';
import { GuideRowProps } from './types';

const GuideRow = ({ guide, program, programProgress }: GuideRowProps): ReactElement => {
	const { formatMessage } = useIntl();
	const { title, description, isLocked, isDisabled, isCompleted, isNextUp, showDuration } = useGuideInfo(
		guide,
		program,
		programProgress,
	);
	const { hideControls, playerMode } = useMediaPlayerContext();

	const duration = useGuideDuration(showDuration ? guide?.asset?.duration : undefined);

	const { activate: onClick, isLive } = useGuideActivation(guide, program, { isLocked, isDisabled });

	return (
		<Wrapper>
			<ClickableWrapper
				isDisabled={hideControls || isDisabled}
				$isDisabled={isDisabled}
				onClick={onClick}
				data-testid="guide-row-clickable-wrapper"
			>
				<RowContent>
					<TextBlock>
						<Title>
							{isCompleted && (
								<StatusGlyph as={circleCheck} role="img" aria-label={formatMessage(messages.completed)} />
							)}
							{!isCompleted && isNextUp && (
								<StatusGlyph as={ringOutline} role="img" aria-label={formatMessage(messages.nextUp)} />
							)}
							{title}
						</Title>
						{description && <Description>{description}</Description>}
						{duration && <Duration>{duration}</Duration>}
					</TextBlock>
					<TrailingSlot>
						{isLocked ? (
							<ContentChip isLocked durationSeconds={undefined} inline />
						) : (
							isLive && (
								<Waveform
									$isPlaying={playerMode === 'playing'}
									role="img"
									aria-label={formatMessage(playerMode === 'playing' ? messages.nowPlaying : messages.paused)}
								>
									<span />
									<span />
									<span />
								</Waveform>
							)
						)}
					</TrailingSlot>
				</RowContent>
			</ClickableWrapper>
			{!hideControls && (
				<FaveHeartContainer>
					<FaveHeart guideId={guide.id} guideTitle={guide.title} />
				</FaveHeartContainer>
			)}
		</Wrapper>
	);
};

export default GuideRow;
